import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useOutletContext } from "react-router-dom";
import { authApi } from "../../api/auth";
import { meApi } from "../../api/me";
import type { UserOut } from "../../api/types";
import { COUNTRY_OPTIONS } from "../../shared/countries";
import { errorMessage } from "../../shared/errors";
import { useInvalidateMe } from "../../shared/hooks/useMe";
import { Alert, Badge, Button, Card, Input } from "../../ui";
import { DeleteAccount } from "./DeleteAccountPage";

export function ProfilePage() {
  const me = useOutletContext<UserOut>();
  const invalidateMe = useInvalidateMe();
  const [form, setForm] = useState({
    first_name: me.first_name,
    last_name: me.last_name ?? "",
    locale: me.locale,
    country: me.country ?? "",
  });
  const [message, setMessage] = useState<{
    kind: "success" | "error";
    text: string;
  } | null>(null);
  const save = useMutation({
    mutationFn: () =>
      meApi.patch({
        ...form,
        last_name: form.last_name,
        country: form.country || undefined,
      }),
    onSuccess: async () => {
      setMessage({ kind: "success", text: "Profile saved." });
      await invalidateMe();
    },
    onError: (err) => setMessage({ kind: "error", text: errorMessage(err) }),
  });
  const resend = useMutation({
    mutationFn: () => authApi.sendVerification(),
    onSuccess: (r) =>
      setMessage({
        kind: "success",
        text: r.sent
          ? "Verification email sent."
          : "Your email is already verified.",
      }),
    onError: (err) => setMessage({ kind: "error", text: errorMessage(err) }),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  return (
    <Card>
      <h1 className="mb-4 text-xl font-semibold">Profile</h1>
      {message ? <Alert kind={message.kind}>{message.text}</Alert> : null}
      <div className="my-4 flex items-center gap-2 text-sm">
        <span>{me.email}</span>
        {me.email_verified ? (
          <Badge tone="success">verified</Badge>
        ) : (
          <>
            <Badge tone="warning">unverified</Badge>
            <Button
              variant="secondary"
              onClick={() => resend.mutate()}
              loading={resend.isPending}
            >
              Resend verification email
            </Button>
          </>
        )}
      </div>
      <form
        onSubmit={submit}
        className="flex max-w-md flex-col gap-4"
        aria-label="Profile"
      >
        <Input
          label="First name"
          required
          value={form.first_name}
          onChange={(e) => setForm({ ...form, first_name: e.target.value })}
        />
        <Input
          label="Last name"
          value={form.last_name}
          onChange={(e) => setForm({ ...form, last_name: e.target.value })}
        />
        <label className="flex flex-col gap-1 text-sm font-medium text-slate-700">
          Country
          <select
            className="rounded-md border border-slate-300 px-3 py-2 text-sm font-normal"
            value={form.country}
            onChange={(e) => setForm({ ...form, country: e.target.value })}
          >
            <option value="">Not set</option>
            {COUNTRY_OPTIONS.map((c) => (
              <option key={c.code} value={c.code}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <Input
          label="Locale"
          value={form.locale}
          onChange={(e) => setForm({ ...form, locale: e.target.value })}
        />
        <Button type="submit" loading={save.isPending}>
          Save
        </Button>
      </form>
      {/* Deleting an account is irreversible, so it sits at the foot of the page, closed. */}
      <details className="mt-8 border-t border-slate-200 pt-4">
        <summary className="cursor-pointer text-sm text-slate-500">
          Delete account
        </summary>
        <div className="mt-3">
          <DeleteAccount />
        </div>
      </details>
    </Card>
  );
}
